import { format, parseISO, addMonths, startOfMonth, isValid } from 'date-fns';
import {
  Plan,
  Application,
  Applicant,
} from 'src/services/interfaces/application';
import { MONTH_MAP } from '@constants/index';
import {
  calculateCoverageLevel,
  normalizeCoverageTags,
} from '@features/onboarding/utils/coverageLevel';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import {
  getCriticalIllnessPriceId,
  PriceSearchConditions,
} from '@features/onboarding/utils/criticalIllnessPriceId';
import { calculateAge } from '@features/onboarding/utils/calculateAge';
import { isActivePrice } from '@utils/planFilters';
import { useApplicationStore } from 'src/store/application';

export function transformApplicationData(
  data: Partial<OnboardingSchema>,
  plans: InsurancePlan[]
): Application {
  if (!data?.applicants?.find) {
    return { applicants: [], income: 0, address: {}, plans: [] };
  }

  const { acceptedTier, tier } = useApplicationStore.getState();
  const selectedPlanKeys = Object.keys(data).filter(
    (key) => key.endsWith('Plan') && !key.endsWith('PlansToCompare')
  );

  const income = data.householdIncome;

  const transformedApplicants = data.applicants.map(
    (applicant: OnboardingSchema['applicants'][0]) => ({
      applicant:
        applicant.applicant === 'primary'
          ? 1
          : applicant.applicant === 'spouse'
            ? 2
            : 3,
      firstName: applicant.firstName?.trim() || '',
      lastName: applicant.lastName?.trim() || '',
      midName: applicant.midName?.trim() || undefined,
      phone:
        applicant.applicant === 'primary'
          ? data.phone?.trim() || undefined
          : undefined,
      gender: applicant.gender,
      dob: `${applicant?.dateOfBirth?.year ?? '1977'}-${MONTH_MAP[applicant?.dateOfBirth?.month] ?? '01'}-${applicant?.dateOfBirth?.day ?? '01'}`,
      nicotine: applicant.nicotine,
      weight: applicant.weight,
      heightFt: applicant.height.ft,
      heightIn: applicant.height.in,
      ssn: applicant.ssn || undefined,
    })
  );
  const hasFemaleApplicants = transformedApplicants.some(
    (applicant) =>
      typeof applicant.gender === 'string' &&
      applicant.gender.toLowerCase() === 'female'
  );

  const getCoverageForPlan = (plan: InsurancePlan): number => {
    if (plan.category === 'Major Medical') {
      const tags = normalizeCoverageTags(
        data.medicalTags || [],
        data.applicants || []
      );
      return calculateCoverageLevel(tags);
    }
    const planTags = (data.planTags || {}) as Record<string, string[]>;
    const tags = normalizeCoverageTags(
      planTags[plan.uid] || [],
      data.applicants || []
    );
    return calculateCoverageLevel(tags);
  };

  const formPlan = selectedPlanKeys
    .map((planKey) => {
      const plan = plans?.find(
        (p) => p.uid === data[planKey as keyof typeof data]
      );
      if (!plan) return null;

      let selectedTier: string | undefined = acceptedTier?.toString() || undefined;
      let selectedCoverageAmount: number | undefined;

      if (plan.category === 'Major Medical') {
        if (data.medicalPlanTier) {
          selectedTier = data.medicalPlanTier;
        }
        if (data.medicalPlanCoverageAmount) {
          selectedCoverageAmount = data.medicalPlanCoverageAmount;
        }
      } else if (plan.category === 'Dental') {
        if (data.dentalPlanTier) {
          selectedTier = data.dentalPlanTier;
        }
        if (data.dentalPlanCoverageAmount) {
          selectedCoverageAmount = data.dentalPlanCoverageAmount;
        }
      } else if (plan.category === 'Critical Illness') {
        const criticalIllnessPlanKey = planKey.replace(
          'Plan',
          'PlanCoverageAmount'
        );
        const coverageAmount = (data as Record<string, unknown>)[
          criticalIllnessPlanKey
        ] as number | undefined;
        if (coverageAmount) {
          selectedCoverageAmount = coverageAmount;
        }
      }

      if (plan.category === 'Critical Illness' && !selectedCoverageAmount) {
        selectedCoverageAmount = 10000;
      }

      if (
        plan.category === 'Critical Illness' &&
        selectedCoverageAmount === undefined
      ) {
        selectedCoverageAmount = 10000;
      }

      let selectedApplicants: number[] = [];

      if (plan.category === 'Major Medical') {
        selectedApplicants = normalizeCoverageTags(
          data.medicalTags || [],
          data.applicants || []
        ).map((e) => +e.replace(/\D/g, ''));
      } else {
        const planTags = data.planTags || {};
        selectedApplicants = normalizeCoverageTags(
          (plan?.uid && planTags[plan?.uid]) || [],
          data.applicants || []
        ).map((e) => +e.replace(/\D/g, ''));
      }
      selectedApplicants = Array.from(new Set([0, ...selectedApplicants]));

      let primaryAge = 30;
      let spouseAge: number | undefined;
      if (data.applicants && data.applicants.length > 0) {
        const primaryApplicant = data.applicants.find(
          (app) => app.applicant === 'primary'
        );
        if (primaryApplicant?.dateOfBirth) {
          primaryAge = calculateAge(primaryApplicant.dateOfBirth) ?? 30;
        }
        const spouseApplicant = data.applicants.find(
          (app) => app.applicant === 'spouse'
        );
        if (spouseApplicant?.dateOfBirth) {
          spouseAge = calculateAge(spouseApplicant.dateOfBirth) ?? undefined;
        }
      }

      let dependentsCount = 0;
      let hasSpouse = false;
      if (plan.category === 'Critical Illness') {
        const planTags = data.planTags || {};
        const planTagsForThisPlan = normalizeCoverageTags(
          planTags[plan.uid] || [],
          data.applicants || []
        );
        dependentsCount = planTagsForThisPlan.filter((tag: string) =>
          tag.includes('dependent')
        ).length;
        hasSpouse = planTagsForThisPlan.some((tag: string) =>
          tag.includes('spouse')
        );
      }

      let priceId: number | undefined;

      if (plan.category === 'Critical Illness') {
        const sanitizeCategory = (category: string) =>
          category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        const depBenefitKey = `${sanitizeCategory(plan.category)}PlanDependentBenefit`;
        const dependentBenefit = ((data as Record<string, unknown>)[depBenefitKey] as number) || 5000;

        const conditions: PriceSearchConditions = {
          coverageAmount: selectedCoverageAmount || 10000,
          primaryAge,
          spouseAge,
          dependentsCount,
          hasSpouse,
          dependentBenefit: dependentsCount > 0 ? dependentBenefit : 0,
        };

        priceId = getCriticalIllnessPriceId(plan, conditions);

        // Если priceId не найден, ищем fallback по coverage level
        if (!priceId && plan.prices && plan.prices.length > 0) {
          const activePrices = plan.prices.filter(isActivePrice);
          const fallbackPrice = activePrices.find(
            (e) => e.coverage === getCoverageForPlan(plan)
          );
          priceId = fallbackPrice?.id || activePrices[0]?.id;
          console.log(
            'fallback priceId:',
            priceId,
            'coverage level:',
            getCoverageForPlan(plan)
          );
        }
      } else {
        // Ищем цену по tier И coverage level
        const coverageLevel = getCoverageForPlan(plan);
        const defaultPlanPrice = plan.prices.filter(isActivePrice).find(
          (e) => e.tier === (acceptedTier?.toString() || selectedTier || tier) && e.coverage === coverageLevel
        );
        priceId = defaultPlanPrice?.id;

        if (!priceId) {
          console.log(
            'Available prices:',
            plan.prices.map((p) => ({
              id: p.id,
              tier: p.tier,
              coverage: p.coverage,
            }))
          );
        }
      }

      const finalPriceId =
        priceId ||
        (() => {
          // Fallback: ищем любую активную цену с нужным coverage level
          if (plan.prices && plan.prices.length > 0) {
            const activePrices = plan.prices.filter(isActivePrice);
            const coverageLevel = getCoverageForPlan(plan);
            const fallbackPrice = activePrices.find(
              (e) => e.coverage === coverageLevel
            );
            return fallbackPrice?.id || activePrices[0]?.id || 0;
          }
          return 0;
        })();

      if (plan.category === 'Major Medical' && data.medicalPlanRejected) {
        return null;
      }

      return {
        id: plan.id,
        priceId: finalPriceId,
        coverage: getCoverageForPlan(plan), // Добавляем правильный coverage level
        // tier: selectedTier || null,
        // coverageAmount:
        //   selectedCoverageAmount && selectedCoverageAmount > 0
        //     ? selectedCoverageAmount.toString()
        //     : '10000',
        applicants: selectedApplicants,
      } as Plan;
    })
    .filter((e): e is Plan => !!e);

  // Preserve existing address and SSN from current application or use from form data
  const existingApplication = useApplicationStore.getState().applications?.[0];
  const address = data.address || existingApplication?.address || {};
  const primaryApplicantSSN = data.applicants?.find(a => a.applicant === 'primary')?.ssn;
  const ssn = primaryApplicantSSN || existingApplication?.ssn || null;

  const application = {
    applicants: transformedApplicants,
    income,
    address,
    plans: formPlan,
    ssn,
  } as Application | any;

  const { phqAcceptedAt, phqRejectedAt, phqUnderReviewAt, activeSubscriptionPlans } = useApplicationStore.getState();
  const isPhqLocked = !!(phqAcceptedAt || phqRejectedAt || phqUnderReviewAt);

  const hasActiveHealthPlan = activeSubscriptionPlans && activeSubscriptionPlans.length > 0 &&
    plans.some(plan =>
      plan.category === 'Major Medical' &&
      activeSubscriptionPlans.some(sub => sub.planId === plan.id)
    );

  const getFirstDayOfNextMonth = (): string => {
    const now = parseISO(format(new Date(), 'yyyy-MM-dd'));
    const nextMonth = addMonths(now, 1);
    const firstDay = startOfMonth(nextMonth);
    return format(firstDay, 'yyyy-MM-dd');
  };

  const isValidDateFormat = (dateStr: string): boolean => {
    if (!dateStr || typeof dateStr !== 'string') return false;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateStr)) return false;
    try {
      const parsed = parseISO(dateStr);
      return isValid(parsed);
    } catch {
      return false;
    }
  };

  // Helper to validate effectiveDate - if in past, return null
  const validateAndCleanEffectiveDate = (effectiveDate: string | null | undefined): string | null => {
    if (!effectiveDate) {
      return null;
    }

    try {
      const parsedDate = parseISO(effectiveDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // If the date is in the past or invalid, return null
      if (!isValid(parsedDate) || parsedDate < today) {
        return null;
      }

      return effectiveDate;
    } catch (error) {
      return null;
    }
  };

  const normalizeEffectiveDate = (rawDate: string | undefined): string => {
    if (!rawDate) {
      return getFirstDayOfNextMonth();
    }
    
    try {
      const dateStr = rawDate.endsWith('Z') 
        ? rawDate.split('T')[0] 
        : rawDate.split('T')[0];
      
      if (isValidDateFormat(dateStr)) {
        return dateStr;
      } else {
        const parsed = parseISO(rawDate.endsWith('Z') ? rawDate : `${rawDate}Z`);
        if (isValid(parsed)) {
          return parsed.toISOString().split('T')[0];
        } else {
          return getFirstDayOfNextMonth();
        }
      }
    } catch {
      return getFirstDayOfNextMonth();
    }
  };

  if (data.healthQuestions && !isPhqLocked && !hasActiveHealthPlan) {
    // --- Normalize effectiveDate to UTC YYYY-MM-DD ---
    const rawEffectiveDate = data.healthQuestions?.coverageStartDate?.effectiveDate;

    // Normalize the date (if empty, uses first day of next month)
    const normalizedDate = normalizeEffectiveDate(rawEffectiveDate);

    // Validate: if date is in the past, set to null. If valid future date, keep it.
    const effectiveDate = validateAndCleanEffectiveDate(normalizedDate);

    const signature = data.healthQuestions?.privacyDisclosure?.signature;
    const rawMedications = (data.healthQuestions?.takingMedicationsNow as any)
      ?.medications;
    const medications = Array.isArray(rawMedications)
      ? rawMedications.filter((med: any) => med?.id).map(
        (med: {
          applicant?: string;
          id: string;
          name?: string;
          dosageId?: string | null;
          dosageValue?: string;
          dosageDescription?: string;
          frequency: string;
          frequencyDescription?: string;
          description?: string;
          medicationType?: string;
          genericName?: string | null;
          genericId?: string | null;
        }) => {
          let applicantNumber = 0;
          if (med.applicant) {
            if (med.applicant === 'primary') {
              applicantNumber = 0;
            } else if (med.applicant === 'spouse') {
              applicantNumber = 1;
            } else if (med.applicant === 'dependent') {
              applicantNumber = 2;
            } else {
              const parsed = parseInt(med.applicant, 10);
              applicantNumber = isNaN(parsed) ? 0 : parsed;
            }
          }

          // Convert "__OTHER__" to null, handle null, or use the actual dosageId value
          // Always include dosageId in the request, even if null
          let dosageId: string | null;
          if (med.dosageId === '__OTHER__' || med.dosageId === null || med.dosageId === undefined) {
            dosageId = null;
          } else if (med.dosageId) {
            dosageId = med.dosageId;
          } else {
            dosageId = null;
          }

          const dosageValue = dosageId
            ? (med.dosageValue || '').toString().trim()
            : '';

          // Determine medication type: use stored value, default to 'generic'
          // 'not_sure' maps to 'brand' for the API (backend only accepts brand/generic)
          let medicationType: 'brand' | 'generic' = 'generic';
          if (med.medicationType === 'brand' || med.medicationType === 'not_sure') {
            medicationType = 'brand';
          } else if (med.medicationType === 'generic') {
            medicationType = 'generic';
          }

          return {
            applicant: applicantNumber,
            id: med.id,
            name: med.name || 'Unknown',
            dosageId,
            dosageValue,
            dosageDescription: (med.dosageDescription || '').toString().trim(),
            frequency: med.frequency,
            frequencyDescription: (med.frequencyDescription || '').toString().trim(),
            description: med.description || '',
            medicationType,
            ...(med.genericName ? { genericName: med.genericName } : {}),
            ...(med.genericId ? { genericId: med.genericId } : {}),
          };
        }
      )
      : [];

    const rawConditions = (
      data.healthQuestions?.surgeriesDiabetesHeartMental as any
    )?.conditions ?? [];
    const rawMedDetails = (data.healthQuestions?.medDetails as any)?.medications ?? [];
    const rawIssues = [
      ...(Array.isArray(rawConditions) ? rawConditions : []),
      ...(Array.isArray(rawMedDetails) ? rawMedDetails : []),
    ];
    const surgeriesIssueDetails =
      (data.healthQuestions?.surgeriesDiabetesHeartMental as any)?.details ||
      {};
    const medIssueDetails =
      (data.healthQuestions?.medDetails as any)?.details || {};
    const getIssueDescription = (...values: unknown[]) => {
      for (const value of values) {
        if (typeof value === 'string' && value.trim()) {
          return value.trim();
        }
      }
      return '';
    };

    const mainIssueKeys = new Set<string>();
    const issuesMap = new Map<string, Array<{ key: string; description: string }>>();

    if (Array.isArray(rawIssues)) {
      rawIssues
        .filter((issue: any) => issue.value && issue.value !== 'none')
        .forEach((issue: any) => {
          const value = issue.value;
          
          if (value.includes('-')) {
            const [mainKey, detailKey] = value.split('-', 2);
            if (mainKey && detailKey && detailKey !== 'none') {
              mainIssueKeys.add(mainKey);
              if (!issuesMap.has(mainKey)) {
                issuesMap.set(mainKey, []);
              }
              
              const description = getIssueDescription(
                medIssueDetails[value],
                surgeriesIssueDetails[value],
                issue.details
              );
              
              const existingDetails = issuesMap.get(mainKey)!;
              if (!existingDetails.some((d) => d.key === detailKey)) {
                existingDetails.push({
                  key: detailKey,
                  description: description,
                });
              }
            }
          } else {
            mainIssueKeys.add(value);
            if (!issuesMap.has(value)) {
              issuesMap.set(value, []);
            }
          }
        });

      const issueDetailsKeys = new Set([
        ...Object.keys(surgeriesIssueDetails),
        ...Object.keys(medIssueDetails),
      ]);

      issueDetailsKeys.forEach((detailKey) => {
        if (detailKey.includes('-')) {
          const [mainKey, detailSubKey] = detailKey.split('-', 2);
          // Skip if detailSubKey is "none" - server doesn't accept it
          if (mainKey && detailSubKey && detailSubKey !== 'none') {
            mainIssueKeys.add(mainKey);
            if (!issuesMap.has(mainKey)) {
              issuesMap.set(mainKey, []);
            }
            
            const descriptionStr = getIssueDescription(
              medIssueDetails[detailKey],
              surgeriesIssueDetails[detailKey]
            );
            
            const existingDetails = issuesMap.get(mainKey)!;
            if (!existingDetails.some((d) => d.key === detailSubKey)) {
              existingDetails.push({
                key: detailSubKey,
                description: descriptionStr,
              });
            }
          }
        }
      });
    }

    const issues = Array.from(mainIssueKeys)
      .map((mainKey) => ({
        key: mainKey,
        details: issuesMap.get(mainKey) || [],
      }))
      .filter((issue) => issue.key && issue.details.length > 0);

    const rawOtherHealthConditions = (data.healthQuestions?.otherHealthConditions as any)
      ?.conditions;
    const otherHealthConditionDetails =
      (data.healthQuestions?.otherHealthConditions as any)?.details || {};

    const otherHealthConditions = Array.isArray(rawOtherHealthConditions)
      ? rawOtherHealthConditions
          .filter(
            (condition: any) => condition?.value && condition.value !== 'none'
          )
          .map((condition: any) => {
            const key = condition.value;
            const rawDescription = otherHealthConditionDetails?.[key];
            const description =
              typeof rawDescription === 'string' ? rawDescription.trim() : '';
            return {
              key,
              description,
            };
          })
          .filter((condition: { key: string }) => Boolean(condition.key))
      : [];

    const pregnancyAnswer = data.healthQuestions?.initialQuestions?.pregnancy;

    application.phq = {
      treatment: data.healthQuestions?.initialQuestions?.treatment || false,
      invalid: data.healthQuestions?.initialQuestions?.invalid || false,
      pregnancy: (pregnancyAnswer ?? (hasFemaleApplicants ? undefined : false)) as boolean,
      effectiveDate,
      disclaimer: data.healthQuestions?.privacyDisclosure?.accepted || false,
      signature: signature || undefined,
      medications: medications,
      issues: issues,
      conditions: otherHealthConditions,
    };
  } else if (isPhqLocked && existingApplication?.phq && !hasActiveHealthPlan) {
    const existingPhq = existingApplication.phq;

    // Check if user selected a new date in the form
    const newEffectiveDate = data.healthQuestions?.coverageStartDate?.effectiveDate;

    let finalEffectiveDate: string | null;

    if (newEffectiveDate) {
      // User selected a new date - validate it
      const normalizedDate = normalizeEffectiveDate(newEffectiveDate);
      finalEffectiveDate = validateAndCleanEffectiveDate(normalizedDate);
    } else {
      // No new date - validate the existing one
      finalEffectiveDate = validateAndCleanEffectiveDate(existingPhq.effectiveDate);
    }

    application.phq = {
      ...existingPhq,
      effectiveDate: finalEffectiveDate,
    };
  }

  return application;
}
